
import { jsPDF } from 'jspdf';

import { isRtlLanguage } from './languageUtils';
import { loadFontForLanguage } from './fontLoader';
import type { Survey } from './surveyDataFetcher';
import type { SupportedLanguage } from './languageUtils';

/**
 * Split text into lines that fit within the page width
 */
const splitTextToFit = (doc: jsPDF, text: string, maxWidth: number): string[] => {
  const lines = doc.splitTextToSize(text, maxWidth);
  return Array.isArray(lines) ? lines : [lines];
};

/**
 * Add text with proper RTL handling and line wrapping
 */
const addTextWithWrapping = (
  doc: jsPDF, 
  text: string, 
  x: number, 
  y: number, 
  maxWidth: number, 
  isRtl: boolean, 
  align?: 'left' | 'right' | 'center'
): number => {
  const lines = splitTextToFit(doc, text, maxWidth);
  let currentY = y;
  
  for (const line of lines) {
    if (isRtl) {
      doc.text(line, x, currentY, { align: align || 'right' });
    } else {
      doc.text(line, x, currentY, { align: align || 'left' });
    }
    currentY += 6; // Line height
  }
  
  return currentY;
};

/**
 * Generate survey questions as PDF
 */
export const generateSurveyQuestionsPDF = async (
  language: SupportedLanguage, 
  selectedSurvey: string,
  surveys: Survey[]
): Promise<ArrayBuffer> => {
  try {
    console.log(`Generating PDF for language: ${language}, survey: ${selectedSurvey}`);
    
    // Check if language is RTL
    const isRtl = isRtlLanguage(language);
    console.log(`RTL mode: ${isRtl}`);
    
    // Create a new PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    // Load appropriate font
    await loadFontForLanguage(doc, language);

    // Set R2L mode for RTL languages
    if (isRtl) {
      doc.setR2L(true);
    }
    
    // Add title - removed survey-specific titles
    doc.setFontSize(18);
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    const contentWidth = pageWidth - (margin * 2);
    
    let yPosition = 30; // Start content higher up since we removed the title
    
    // Add each survey's questions
    for (const survey of surveys) {
      // Add survey title
      doc.setFontSize(14);
      const title = survey.title?.[language] || Object.values(survey.title || {})[0] || "Untitled Survey";
      
      console.log(`Adding survey title: ${title}`);
      
      // Use appropriate alignment based on text direction
      const titleAlign = isRtl ? 'right' : 'left';
      const surveyTitleX = isRtl ? pageWidth - margin : margin;
      yPosition = addTextWithWrapping(doc, title, surveyTitleX, yPosition, contentWidth, isRtl, titleAlign);
      yPosition += 10;
      
      // Get questions for this language
      const questions = survey.questions?.[language] as Array<{
        id: string;
        type: string;
        question: string;
        required: boolean;
        options?: string[];
      }> || [];
      
      console.log(`Processing ${questions.length} questions`);
      
      // Add each question
      doc.setFontSize(11);
      for (const q of questions) {
        console.log(`Adding question: ${q.question.substring(0, 50)}...`);
        
        // Check if we need a new page
        if (yPosition > 260) {
          doc.addPage();
          yPosition = 20;
        }
        
        // Add question text with wrapping
        const questionX = isRtl ? pageWidth - margin : margin;
        yPosition = addTextWithWrapping(doc, q.question, questionX, yPosition, contentWidth, isRtl, titleAlign);
        yPosition += 4;
        
        // For multiple choice questions
        if ((q.type === 'radio' || q.type === 'checkbox') && q.options) {
          for (const option of q.options) {
            // Check if we need a new page for options
            if (yPosition > 270) {
              doc.addPage();
              yPosition = 20;
            }
            
            // Use appropriate symbols and positioning for RTL/LTR
            // For RTL languages, flip the symbols to maintain proper visual flow
            const symbol = q.type === 'radio' ? 
              (isRtl ? ') (' : '( )') : 
              (isRtl ? '] [' : '[ ]');
            
            if (isRtl) {
              // For RTL, put symbol on the right side
              doc.text(symbol, pageWidth - margin, yPosition);
              yPosition = addTextWithWrapping(doc, option, pageWidth - margin - 15, yPosition, contentWidth - 15, isRtl, 'right');
            } else {
              // For LTR, put symbol on the left side  
              doc.text(symbol, margin, yPosition);
              yPosition = addTextWithWrapping(doc, option, margin + 15, yPosition, contentWidth - 15, isRtl, 'left');
            }
            // Removed space between options for tighter layout
          }
          yPosition += 4; // Extra space after options
        } else if (q.type === 'text' || q.type === 'number') {
          // For text/number questions, add one line for writing
          const lineY1 = yPosition + 5;
          
          if (isRtl) {
            doc.line(margin, lineY1, pageWidth - margin - 20, lineY1);
          } else {
            doc.line(margin + 20, lineY1, pageWidth - margin, lineY1);
          }
          yPosition += 10;
        }
        
        yPosition += 2; // Space between questions
      }
      
      yPosition += 10; // Space between surveys
    }
    
    // Add instructions at the end (removed language information)
    doc.setFontSize(10);
    if (yPosition > 270) {
      doc.addPage();
      yPosition = 20;
    }
    
    // Use appropriate alignment based on text direction
    const titleAlign = isRtl ? 'right' : 'left';
    const langInfoX = isRtl ? pageWidth - margin : margin;
    
    // Updated instructions with proper RTL symbols
    const instructions = isRtl ? 
      "تعليمات: ضع علامة على إجابتك عن طريق ملء ) ( للخيارات أو ] [ لمربعات الاختيار" :
      "Instruktioner: Markera ditt svar genom att fylla i ( ) för val eller [ ] för kryssrutor";
    
    yPosition = addTextWithWrapping(doc, instructions, langInfoX, yPosition, contentWidth, isRtl, titleAlign);
    
    console.log('PDF generation completed successfully');
    
    // Return the PDF as bytes
    return doc.output('arraybuffer');
  } catch (error) {
    console.error("Error generating survey questions PDF:", error);
    throw error;
  }
};
